import { useDeleteRoomResourceMutation, useSaveRoomResourceMutation } from "@crowdcoursing/api/graphql";
import { useCallback } from "react";

import { useToast } from "./useToast";

export interface ResourceItem {
  id: string;
  name: string;
  description: string;
  url: string;
}

export interface NewResourceItem {
  name: string;
  description: string;
  url: string;
}

export const useRoomResourceManagement = (roomId: string, onSuccess?: () => void) => {
  const toast = useToast();

  const { mutate: saveResourceMutation, isPending: isSaving } = useSaveRoomResourceMutation({
    onError: (error) => {
      toast.error("Failed to save resource");
      console.error("Save resource error:", error);
    },
    onSuccess: () => {
      toast.success("Resource saved successfully!");
      onSuccess?.();
    },
  });

  const { mutate: deleteResourceMutation, isPending: isDeleting } = useDeleteRoomResourceMutation({
    onError: (error) => {
      toast.error("Failed to delete resource");
      console.error("Delete resource error:", error);
    },
    onSuccess: () => {
      toast.success("Resource deleted successfully!");
      onSuccess?.();
    },
  });

  const saveResource = useCallback(
    (newItem: NewResourceItem, oldItem?: ResourceItem) => {
      saveResourceMutation({
        request: {
          id: oldItem?.id,
          roomId,
          ...newItem,
        },
      });
    },
    [saveResourceMutation, roomId],
  );

  const deleteResource = useCallback(
    (item: ResourceItem) => {
      deleteResourceMutation({
        request: {
          id: item.id,
          roomId,
        },
      });
    },
    [deleteResourceMutation, roomId],
  );

  return {
    deleteResource,
    isDeleting,
    isLoading: isSaving || isDeleting,
    isSaving,
    saveResource,
  };
};
